// src/components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { getActiveRole } from '../utils/auth';
import axios from 'axios';
import { useMobileNav } from '../context/MobileNavContext';

const Sidebar = () => {
    const role = getActiveRole();
    const [licenseInfo, setLicenseInfo] = useState(null);
    const { isOpen, close } = useMobileNav();

    useEffect(() => {
        axios.get('/api/public/license/info')
            .then(res => setLicenseInfo(res.data))
            .catch(() => {/* silently ignore — sidebar still works */ });
    }, []);

    const navItemClass = ({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 min-h-[44px] ${isActive
            ? 'bg-primary-600 text-white shadow-sm'
            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`;

    const dashboardPath =
        role === 'ADMIN' ? '/admin' :
            role === 'MANAGER' ? '/manager' :
                role === 'COACH' ? '/coach' :
                    role === 'STUDENT' ? '/student' :
                        '/';

    const Section = ({ title, icon, children }) => (
        <div className="mb-6">
            <div className="flex items-center gap-2 px-3 mb-3">
                {icon && <span className="text-gray-500">{icon}</span>}
                <div className="text-xs uppercase text-gray-500 font-semibold tracking-wider">{title}</div>
            </div>
            <div className="space-y-1">{children}</div>
        </div>
    );

    const NavItem = ({ to, icon, children }) => (
        <NavLink to={to} className={navItemClass} onClick={close}>
            {icon && <span className="flex-shrink-0 w-5 h-5">{icon}</span>}
            <span className="text-sm font-medium">{children}</span>
        </NavLink>
    );

    const icons = {
        dashboard: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
        ),
        branches: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
        ),
        users: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
        ),
        levels: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
        ),
        batches: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
        ),
        enrollments: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
        ),
        sessions: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
        ),
        reports: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
        ),
        wallet: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
        ),
    };

    const asideClasses = [
        'w-64 max-w-[85vw] bg-gradient-to-b from-gray-900 to-gray-800 text-white flex flex-col shadow-xl border-r border-gray-700',
        'lg:relative lg:translate-x-0 lg:flex-shrink-0 lg:self-stretch',
        'fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 ease-out',
        'top-[3.75rem] lg:top-0 h-[calc(100%-3.75rem)] lg:h-auto',
        isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
    ].join(' ');

    return (
        <>
            {isOpen && (
                <button
                    type="button"
                    className="fixed inset-0 z-30 bg-black/50 lg:hidden"
                    aria-label="Close navigation menu"
                    onClick={close}
                />
            )}

            <aside id="app-sidebar" className={asideClasses}>
                <div className="p-4 border-b border-gray-700">
                    <NavLink
                        to={dashboardPath}
                        onClick={close}
                        className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg min-h-[44px]"
                    >
                        <span className="w-5 h-5 flex-shrink-0">{icons.dashboard}</span>
                        <span className="text-lg font-bold">Dashboard</span>
                    </NavLink>
                </div>

                <nav className="flex-1 overflow-y-auto p-4 scrollbar-thin" aria-label="Main">
                    {role === 'ADMIN' && (
                        <>
                            <Section title="Manage">
                                <NavItem to="/admin/branches" icon={icons.branches}>Branches</NavItem>
                                <NavItem to="/admin/managers" icon={icons.users}>Managers</NavItem>
                                <NavItem to="/admin/coaches" icon={icons.users}>Coaches</NavItem>
                                <NavItem to="/admin/students" icon={icons.users}>Students</NavItem>
                                <NavItem to="/admin/levels" icon={icons.levels}>Levels</NavItem>
                                <NavItem to="/admin/batches" icon={icons.batches}>Batches</NavItem>
                                <NavItem to="/admin/enrollments" icon={icons.enrollments}>Enrollments</NavItem>
                                <NavItem to="/admin/sessions" icon={icons.sessions}>Sessions</NavItem>
                                <NavItem to="/admin/discounts" icon={(
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                    </svg>
                                )}>Discounts</NavItem>
                            </Section>

                            <Section title="Reports">
                                <NavItem to="/admin/reports/coach-revenue" icon={icons.reports}>Coach Revenue & Commission</NavItem>
                                <NavItem to="/admin/reports/coach-settlement" icon={icons.reports}>Coach Settlement Dues</NavItem>
                                <NavItem to="/admin/reports/session-statement" icon={icons.reports}>Student Session Statement</NavItem>
                                <NavItem to="/admin/reports/sessions-summary" icon={icons.reports}>Sessions Summary</NavItem>
                                <NavItem to="/admin/reports/session-attendance" icon={icons.reports}>Batch-wise Attendance</NavItem>
                                <NavItem to="/admin/reports/monthly-statements" icon={icons.reports}>Monthly Statements</NavItem>
                            </Section>

                            <Section title="Fees & Billing">
                                <NavItem to="/admin/advance-payment" icon={icons.wallet}>Wallet Top-up</NavItem>
                            </Section>

                            <Section title="System">
                                <NavItem to="/admin/audit-logs" icon={icons.reports}>Audit Logs</NavItem>
                            </Section>

                            <Section title="Calendar">
                                <NavItem to="/admin/calendar" icon={icons.sessions}>Session Calendar</NavItem>
                                <NavItem to="/admin/holidays" icon={icons.sessions}>Holiday Management</NavItem>
                            </Section>
                        </>
                    )}

                    {role === 'MANAGER' && (
                        <>
                            <Section title="Manage">
                                <NavItem to="/manager/branches" icon={icons.branches}>Branches</NavItem>
                                <NavItem to="/manager/levels" icon={icons.levels}>Levels</NavItem>
                                <NavItem to="/manager/batches" icon={icons.batches}>Batches</NavItem>
                                <NavItem to="/manager/coaches" icon={icons.users}>Coaches</NavItem>
                                <NavItem to="/manager/students" icon={icons.users}>Students</NavItem>
                                <NavItem to="/manager/enrollments" icon={icons.enrollments}>Enrollments</NavItem>
                                <NavItem to="/manager/sessions" icon={icons.sessions}>Sessions</NavItem>
                            </Section>

                            <Section title="Reports">
                                <NavItem to="/manager/reports/session-statement" icon={icons.reports}>Session Statement</NavItem>
                                <NavItem to="/manager/reports/session-attendance" icon={icons.reports}>Batch-wise Payments</NavItem>
                                <NavItem to="/manager/reports/coach-revenue" icon={icons.reports}>Coach Revenue & Commission</NavItem>
                            </Section>

                            <Section title="Fees & Billing">
                                <NavItem to="/manager/advance-payment" icon={icons.wallet}>Wallet Top-up</NavItem>
                            </Section>

                            <Section title="Calendar">
                                <NavItem to="/manager/calendar" icon={icons.sessions}>Session Calendar</NavItem>
                            </Section>
                        </>
                    )}

                    {role === 'COACH' && (
                        <>
                            <Section title="Manage">
                                <NavItem to="/coach/students" icon={icons.users}>Students</NavItem>
                                <NavItem to="/coach/levels" icon={icons.levels}>Levels</NavItem>
                                <NavItem to="/coach/sessions" icon={icons.sessions}>Sessions</NavItem>
                                <NavItem to="/coach/enrollments" icon={icons.enrollments}>Enrollments</NavItem>
                            </Section>

                            <Section title="Reports">
                                <NavItem to="/coach/reports/session-statement" icon={icons.reports}>Session Statement</NavItem>
                            </Section>

                            <Section title="Calendar">
                                <NavItem to="/coach/calendar" icon={icons.sessions}>Session Calendar</NavItem>
                            </Section>
                        </>
                    )}

                    {role === 'STUDENT' && (
                        <>
                            <Section title="My Account">
                                <NavItem to="/student/sessions" icon={icons.sessions}>My Sessions</NavItem>
                                <NavItem to="/student/enrollments" icon={icons.enrollments}>My Enrollments</NavItem>
                                <NavItem to="/student/wallet" icon={icons.wallet}>My Wallet</NavItem>
                                <NavItem to="/student/session-statement" icon={icons.reports}>Session Statement</NavItem>
                                <NavItem to="/student/calendar" icon={icons.sessions}>Session Calendar</NavItem>
                            </Section>
                        </>
                    )}
                </nav>

                <div className="p-4 border-t border-gray-700 space-y-3">
                    <div className="rounded-md bg-gray-700/40 px-3 py-2.5 space-y-1.5">
                        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Developed &amp; Licensed by</p>
                        <p className="text-sm font-semibold text-gray-300">Axeron Technologies</p>
                        <a
                            href="mailto:axerontechnologies@gmail.com"
                            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors min-h-[44px]"
                        >
                            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            axerontechnologies@gmail.com
                        </a>
                        <a
                            href="tel:+919426118580"
                            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors min-h-[44px]"
                        >
                            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.042 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                            </svg>
                            +91 94261 18580
                        </a>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-600">© 2026 Apex Chess Academy</span>
                        <span className="text-xs text-gray-600">{licenseInfo ? `v${licenseInfo.version}` : 'v1.0.0'}</span>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
